// The paywall micro-copy matrix (issue #1478).
//
// Every gate in the app already declares *what* was blocked and *which
// control* blocked it -- see show-paywall.js for that vocabulary. This file is
// the other half: the words the upgrade modal shows for each of those, in one
// place, instead of a headline hard-coded per tier in UpgradePlanBody.
//
// Resolution runs most specific first:
//
//   featureName:trigger  ->  trigger  ->  featureName  ->  null
//
// Returning null when nothing matches is the point, not a gap: UpgradePlanBody
// falls back to the per-tier copy it has always shown. The matrix describes
// particular cases, not every gate in the app, and a gate it does not describe
// has to say something generic rather than borrow the nearest row -- borrowed
// copy is what made the first pass wrong (#1478 comment 2), because a row that
// counts schedules is false when the click was about time configuration.
// Whether a row may travel beyond its exact trigger is decided per row, below.
//
// Copy strings may carry {placeholders}. They are filled from the `vars` a
// call site passes through showPaywall, falling back to the entry's own
// `vars` defaults -- which are the numbers the spec was written against -- so
// a string never renders a raw token even when nothing is threaded in.

// Rendered as: <strong>{subtextTag}</strong> {subtext}. The tag is the bolded
// lead-in from the spec's subtext column ("Instant Line-Item Budgets."), split
// out rather than parsed off the first period so a sentence that happens to
// contain ".fdx" or "1-Click" cannot be cut in the wrong place.
export const PAYWALL_COPY = {
  projects: {
    headline: 'Create Unlimited Projects',
    subtext: "You've reached your free {used}/{limit} project limit.",
    bullet: 'Create Unlimited Projects',
    vars: { used: '1', limit: '1' }
  },
  scripts: {
    headline: 'Create Unlimited Scripts',
    subtext: 'Free accounts are limited to 1 script.',
    bullet: 'Create Unlimited Scripts'
  },
  credits: {
    headline: 'Keep Creating with Unlimited Credits',
    subtext: "You've used your {used}/{limit} free credits.",
    bullet: 'Unlock Unlimited AI Credits',
    vars: { used: '100', limit: '100' }
  },
  aiImages: {
    headline: 'Create Unlimited AI Images',
    subtext: "You've generated your {used}/{limit} free AI images.",
    bullet: 'Unlock Unlimited AI Images',
    vars: { used: '5', limit: '5' }
  },
  aiVideo: {
    headline: 'Visualize Your Script with AI Video',
    subtext: 'AI Video is locked on the Free plan.',
    bullet: 'Unlock AI Video Generation'
  },
  scriptExport: {
    headline: 'Export Clean Scripts',
    subtext: 'Free PDF exports include FinalBit watermarks.',
    bullet: 'Watermark-Free Exports'
  },
  budgeting: {
    headline: 'Budget Your Film in Seconds',
    subtextTag: 'Instant Line-Item Budgets.',
    subtext: 'Get topsheet totals, line-item costs, and tax credit estimates with 1-click.',
    bullet: 'Create Production Budgets'
  },
  // No gate counts schedules today, so nothing resolves to this row. Kept
  // because the spec describes the case: wire it to that trigger when the
  // second-schedule limit is actually enforced, rather than letting the other
  // scheduling gates borrow it in the meantime.
  scheduling: {
    headline: 'Create Unlimited Schedules',
    subtext: 'Free accounts are limited to 1 shooting schedule.',
    bullet: 'Create Unlimited Schedules'
  },
  agenticScheduling: {
    headline: 'Unlock Neil, Your AI 1st AD',
    subtext: 'Ask Neil to organize stripboards, and resolve conflicts.',
    bullet: 'Unlock AI 1st AD Assistant'
  },
  breakdown: {
    headline: 'Break Down Entire Script in 1-Click',
    subtextTag: 'Save hours.',
    subtext: 'Breakdown your entire script with 1-Click.',
    bullet: 'Unlock Bulk AI Breakdowns'
  },
  storyboard: {
    headline: 'Create Full Storyboards in 1-Click',
    subtextTag: 'Zero sketching.',
    subtext: "Generate multi-angle storyboard frames matching your film's exact visual style.",
    bullet: 'Create Full AI Storyboards'
  },
  tableRead: {
    headline: 'Run an Instant AI Table Read',
    subtextTag: 'Full Cast AI Read.',
    subtext: 'Test dialogue flow and scene pacing with realistic multi-character AI voices.',
    bullet: 'Create a Table Read with AI Cast'
  },
  pitchDeck: {
    headline: 'Access Premium Templates',
    subtextTag: 'Unlimited Pro Library.',
    subtext: 'Unlock all pitch decks, and high quality images for every genre.',
    bullet: 'Unlock Pro Templates'
  },
  plotHole: {
    headline: 'Catch Script Flaws Early',
    subtextTag: '{limit} Script Audits/Mo.',
    subtext: 'Detect logic gaps, character flaws, and plotholes instantly.',
    bullet: 'Create In-Depth Script Audits',
    vars: { limit: '10' }
  },
  scriptCoverage: {
    headline: 'Generate Instant Coverage',
    subtextTag: '{limit} Reports Included.',
    subtext: 'Get instant producer-level script analysis, character breakdown, and story notes.',
    bullet: 'Create Producer Coverage Reports',
    vars: { limit: '10' }
  },
  scriptDoctor: {
    headline: 'Clean Up Your Screenplay',
    subtextTag: 'Instant Script Polish.',
    subtext: 'Automatically fix margins, character tags, and scene layout errors.',
    bullet: 'Unlock Script Doctor for Format Evaluation'
  },
  proAiModels: {
    headline: 'Unlock Advanced AI Models',
    subtextTag: 'Next-Gen Performance.',
    subtext: 'Access hyper-realistic image generation and faster, more nuanced script chat responses.',
    bullet: 'Unlock Advanced AI Models'
  },
  fdxExport: {
    headline: 'Export to Final Draft',
    subtextTag: 'Industry Standard FDX.',
    subtext: 'Seamlessly export fully formatted .fdx files ready for production, casting, and industry software.',
    bullet: 'Unlock Final Draft (.FDX) Exports'
  }
}

// Rows whose subtext states a specific limit -- a count, a quota, a balance --
// may only fire on the exact control that hits that limit. "Free accounts are
// limited to 1 shooting schedule" shown to someone clicking time-scheduling
// configs is not merely off-tone, it is false, and that is what made the first
// pass misleading: these rows used to travel to every gate their feature had.
// So they are listed trigger by trigger here, and a trigger nobody listed gets
// no copy at all.
export const COPY_BY_TRIGGER = {
  // Projects -- attempting a second project.
  project_wizard: 'projects',
  create_feature_film: 'projects',
  create_episodic_pilot: 'projects',
  create_video_short: 'projects',
  feature_film_form: 'projects',
  episodic_pilot_form: 'projects',
  navigator_workflow: 'projects',
  treatment_error: 'projects',
  pilot_treatment_error: 'projects',
  // Scripts -- a second script inside a project that already exists.
  create_script: 'scripts',
  duplicate_script: 'scripts',
  create_script_from_treatment: 'scripts',
  create_script_from_pilot_treatment: 'scripts',
  // AI credits -- a server-side LIMIT_EXCEEDED on an AI call.
  copilot_send_message: 'credits',
  copilot_open_chat: 'credits',
  copilot_new_chat: 'credits',
  navigator_message: 'credits',
  company_brain: 'credits',
  ask_ai: 'credits',
  change_accent: 'credits',
  // AI images -- three features, one image quota.
  generate_image: 'aiImages',
  image_generation_limit: 'aiImages',
  generate_shot_image: 'aiImages',
  // Draw Scene's LIMIT_EXCEEDED is the image quota, not the storyboard gate.
  draw_scene: 'aiImages',
  // Exports. The `watermark` entitlement gates both, and FeatureValidation's
  // trackAs is what tells the PDF watermark apart from the FDX export.
  watermark: 'scriptExport',
  export_fdx: 'fdxExport',
  // Named in the matrix as their own rows rather than as their feature's.
  ai_sorting: 'agenticScheduling',
  schedule_sorting: 'agenticScheduling',
  model_selector: 'proAiModels',
  script_doctor: 'scriptDoctor',
  pro_deck_asset: 'pitchDeck'
}

// One trigger, two features, two cases: creating a video project is the AI
// Video pitch when the plan lacks the feature and the project-limit pitch when
// the account is out of projects. Consulted before COPY_BY_TRIGGER.
export const COPY_BY_FEATURE_TRIGGER = {
  'projectCreation:create_video_project': 'projects',
  'aiVideo:create_video_project': 'aiVideo'
}

// Rows whose subtext is a value proposition rather than a claim about the
// account's usage. These say what the tier buys, so they stay true from any
// control that opens the feature -- a Tools menu item, a hub card, a route
// guard -- and are mapped by feature rather than one trigger at a time.
//
// Deliberately absent, and defaulting instead:
//   projectCreation, editor  their rows count projects and scripts, so they
//                            are listed per trigger above; the hub cards and
//                            route guards under `editor` are the free
//                            one-script access rule, which is a different
//                            thing from being told to create more scripts
//   scheduling               "limited to 1 shooting schedule" is a count, and
//                            most scheduling gates (time configs, settings,
//                            durations, Excel/DOOD exports) are not about the
//                            number of schedules at all -- #1478 comment 2
export const COPY_BY_FEATURE = {
  aiVideo: 'aiVideo',
  budgeting: 'budgeting',
  breakdown: 'breakdown',
  storyboard: 'storyboard',
  tableRead: 'tableRead',
  pitchDeck: 'pitchDeck',
  plotHole: 'plotHole',
  scriptCoverage: 'scriptCoverage',
  scriptDoctor: 'scriptDoctor'
}

// Left as-is when a placeholder has no value anywhere: a visible {token} in
// staging is a louder bug report than a silently emptied sentence in
// production would be, and every shipped string has a default.
export const interpolate = (template, vars = {}) => {
  if (typeof template !== 'string') return template
  return template.replace(/\{(\w+)\}/g, (match, key) => (
    vars[key] === undefined || vars[key] === null ? match : String(vars[key])
  ))
}

export const resolveCopyKey = ({ featureName, trigger } = {}) =>
  COPY_BY_FEATURE_TRIGGER[`${featureName}:${trigger}`] ||
  COPY_BY_TRIGGER[trigger] ||
  COPY_BY_FEATURE[featureName] ||
  null

// Returns null -- never a partial object -- when the matrix has nothing for
// this gate, so the caller can tell "no entry" from "entry with no subtext".
export const resolvePaywallCopy = ({ featureName, trigger, vars } = {}) => {
  const entry = PAYWALL_COPY[resolveCopyKey({ featureName, trigger })]
  if (!entry) return null
  const values = { ...entry.vars, ...vars }
  return {
    headline: interpolate(entry.headline, values),
    subtextTag: interpolate(entry.subtextTag, values),
    subtext: interpolate(entry.subtext, values),
    bullet: interpolate(entry.bullet, values)
  }
}

export default resolvePaywallCopy
